import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { FilterCondition } from 'framework/types';
import { DateInput, FormFieldsData } from 'framework/form';
import { Box } from '@mui/material';
import { FloatingPlacement } from 'framework/ui/uiContract';
import { useDateService, GREATER_OR_EQUAL, LESS_OR_EQUAL, DEFAULT_FORMAT } from '../../utils/hooks/useDateService';
import { useUiTheme } from 'framework/uiTheme';
import useDateRangeFilter from './useDateRangeFilter';

type FilterConditionUnion = FilterCondition.GE | FilterCondition.LE;
type FilterPair = Partial<Record<FilterConditionUnion, string>>;

interface DateRangeFilterForChipProps {
  source: string;
  dateFormat?: string;
  canBeInFuture?: boolean;
  label?: string;
  popperPlacement?: FloatingPlacement;
  onChangeCallback?: (obj: { [key: string]: string[] }) => void;
  value?: string[];
}

const DateRangeFilterForChip: React.FC<DateRangeFilterForChipProps> = ({
  source,
  dateFormat = DEFAULT_FORMAT,
  canBeInFuture = false,
  label,
  popperPlacement,
  onChangeCallback,
  value,
}) => {
  const { DateRangeFilterLayout } = useUiTheme();
  const { formatDateForAPI } = useDateService();
  const {
    getErrors,
    fromApiToFrontFormat,
    localDate,
    getMaxDate,
    getMaxGeDate,
    getMinGeDate,
    getMaxLeDate,
    getMinLeDate,
  } = useDateRangeFilter(dateFormat, dateFormat, canBeInFuture, source);

  const [range, setRange] = useState<[string | null, string | null]>([value?.[0] || null, value?.[1] || null]);
  const [fromValue, untilValue] = range;

  const MAX_DATE = getMaxDate();
  const hasError = !isEmpty([...getErrors(GREATER_OR_EQUAL), ...getErrors(LESS_OR_EQUAL)]);
  const invalidClass = hasError ? 'is-invalid' : '';

  const handleDateChange = (condition: FilterConditionUnion, formFieldsData: FormFieldsData) => {
    const sourceKey = condition;
    const formattedDate = formatDateForAPI(formFieldsData[sourceKey]);

    const newRange: [string | null, string | null] = [...range];

    if (condition === GREATER_OR_EQUAL) newRange[0] = formattedDate;
    if (condition === LESS_OR_EQUAL) newRange[1] = formattedDate;

    if (newRange[0] === range[0] && newRange[1] === range[1]) return;

    setRange(newRange);

    if (onChangeCallback) {
      onChangeCallback({
        [source]: [newRange[0] || '', newRange[1] || ''],
      });
    }
  };

  useEffect(() => {
    const nextRange: [string | null, string | null] = [value?.[0] || null, value?.[1] || null];

    setRange(prevRange => (prevRange[0] === nextRange[0] && prevRange[1] === nextRange[1] ? prevRange : nextRange));
  }, [value]);

  return (
    <DateRangeFilterLayout
      label={label}
      geValue={fromValue || ''}
      leValue={untilValue || ''}
      source={source}
      className={invalidClass}
      sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 0, marginBottom: 0 }}
      popperPlacement={popperPlacement}
      onChangeCallback={(filterPair: FilterPair) => {
        const newRange = [...range] as [string | null, string | null];

        if (filterPair.ge !== undefined) newRange[0] = filterPair.ge;
        if (filterPair.le !== undefined) newRange[1] = filterPair.le;

        setRange(newRange);
        if (onChangeCallback) {
          onChangeCallback({ [source]: [newRange[0] || '', newRange[1] || ''] });
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, width: '100%' }}>
        <DateInput
          toggleButton={
            <>
              <i className="fa fa-calendar" aria-hidden style={{ marginRight: 1 }} />
              From:
            </>
          }
          label="From"
          source={FilterCondition.GE}
          value={fromValue ? fromApiToFrontFormat(fromValue) : ''}
          onChangeCallback={(values: FormFieldsData) => handleDateChange(FilterCondition.GE, values)}
          dateFormat={dateFormat}
          maxDate={getMaxGeDate() || (untilValue && localDate(fromApiToFrontFormat(untilValue))) || MAX_DATE}
          minDate={getMinGeDate()}
          errorMessages={getErrors(GREATER_OR_EQUAL)}
        />

        <DateInput
          toggleButton="Until:"
          label="Until"
          source={FilterCondition.LE}
          value={untilValue ? fromApiToFrontFormat(untilValue) : ''}
          onChangeCallback={(values: FormFieldsData) => handleDateChange(FilterCondition.LE, values)}
          dateFormat={dateFormat}
          maxDate={getMaxLeDate() || MAX_DATE}
          minDate={getMinLeDate() || (fromValue && localDate(fromApiToFrontFormat(fromValue)))}
          errorMessages={getErrors(LESS_OR_EQUAL)}
        />
      </Box>
    </DateRangeFilterLayout>
  );
};

export default DateRangeFilterForChip;
